import { Dispatch, useMemo, useState } from 'react'
import { PiWarning, PiCircleNotch } from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import { getColumnOption } from 'features/importerv2/columnMappingShared'
import { InternalColumns } from 'features/importerv2/importer-types'
import { updateImportPreview } from 'features/importerv2/importer-api'
import { toRowsJSON } from 'features/importerv2/ImporterViewModel'
import {
  validateContactRows,
  findInFileDuplicateRowIds,
  RowIssue,
} from '../contactValidation'
import { setCellByRowId } from '../rowEdit'
import { WizardState, WizardAction } from '../wizardState'
import { EditableCell } from './EditableCell'
import { ValidationLegend } from './ValidationLegend'
import { DecisionBar } from './DecisionBar'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FilterTab =
  | 'all'
  | 'included'
  | 'excluded'
  | 'errors'
  | 'warnings'
  | 'duplicates'

interface TabDef {
  key: FilterTab
  label: string
  count: number
}

// ---------------------------------------------------------------------------
// Helpers (module-level, no React dependency)
// ---------------------------------------------------------------------------

/** Returns the human-readable label for a mapped field key. */
function fieldLabel(key: string): string {
  return getColumnOption(key)?.name ?? key
}

/**
 * Returns the unique set of active (non-ignore) headers in CSV order,
 * deduplicating by mapped field in case the same logical field was mapped
 * from multiple CSV columns.
 */
function getActiveColumns(
  headers: { original: string; mapped: string }[]
): { original: string; mapped: string }[] {
  const seen = new Set<string>()
  const result: { original: string; mapped: string }[] = []
  for (const h of headers) {
    if (h.mapped !== 'ignore' && !seen.has(h.mapped)) {
      seen.add(h.mapped)
      result.push({ original: h.original, mapped: h.mapped })
    }
  }
  return result
}

/** True when the row has at least one error-severity issue. */
function rowHasError(issues: RowIssue[]): boolean {
  return issues.some((i) => i.severity === 'error')
}

/** True when the row has at least one warning-severity issue. */
function rowHasWarning(issues: RowIssue[]): boolean {
  return issues.some((i) => i.severity === 'warning')
}

/** Which flavour(s) of duplicate a row is. */
interface DuplicateKind {
  inFile: boolean
  crm: boolean
}

/**
 * Hover copy explaining the duplicate kind. The CRM tail is mode-dependent so
 * the user understands what will happen to the matching contact on import.
 */
function duplicateTooltip(
  kind: DuplicateKind,
  mode: 'keep' | 'update'
): string {
  const crmTail =
    mode === 'update'
      ? ' These contacts will be updated with the values in your file.'
      : ' These rows are skipped; existing contacts are left unchanged.'
  if (kind.inFile && kind.crm) {
    return (
      'Already in your CRM, and appears more than once in this file.' + crmTail
    )
  }
  if (kind.crm) {
    return 'Already in your CRM.' + crmTail
  }
  return 'This email appears more than once in this file. Rows that are otherwise identical merge into one contact; rows that share the email but differ (e.g. name or phone) import as separate contacts.'
}

/**
 * Logical email fields a duplicate marker can attach to. A duplicate row gets a
 * synthesized 'duplicate' issue on whichever of these columns it has a value in,
 * so the email cell itself reads red with the explanatory hover tooltip.
 */
const EMAIL_LOGICAL_FIELDS = [
  'contact_work_email',
  'contact_personal_email',
  'contact_generic_email',
]

// ---------------------------------------------------------------------------
// ValidateStep
// ---------------------------------------------------------------------------

interface ValidateStepProps {
  state: WizardState
  dispatch: Dispatch<WizardAction>
}

/**
 * Step 3 of the contact import wizard ("Validate Data").
 *
 * Renders an editable grid of imported rows with:
 * - Per-cell error/warning styling (wavy underline + ⚠ icon), derived from
 *   contactValidation.ts each render — no useEffect.
 * - Row include / exclude toggle (controlled by wizardReducer.excludedRowIds).
 * - Six filter tabs: All rows · Included · Excluded · Errors · Warnings · Duplicates.
 * - A "Review" decision bar (shown when there are errors or CRM duplicates):
 *   errored rows are ALWAYS excluded (no choice). CRM duplicates follow the
 *   Keep/Update mode: "keep" EXCLUDES them (skipped — existing contacts
 *   untouched, nothing imported/linked), "update" INCLUDES them (existing
 *   contacts updated). In-file duplicates are never excluded — the backend
 *   merges them into one contact.
 *
 * The Duplicates tab/count is the union of in-file duplicates (same email on
 * 2+ rows) and CRM duplicates (IS_EXISTING_CRM_CONTACT). Each duplicate row
 * carries a hover affordance distinguishing the two.
 *
 * The effective excluded set (manual exclusions ∪ errored rows ∪ CRM duplicates
 * when mode is "keep") drives the displayed counts and is persisted to wizard
 * state on "Next" so the ReviewImportStep imports exactly the rows shown here as
 * included.
 *
 * ImporterDataTable is NOT used here: it always prepends its own internal
 * checkbox column and manages selection privately, which cannot be reconciled
 * with our reducer-owned include/exclude model. A focused table is simpler.
 */
export function ValidateStep({ state, dispatch }: ValidateStepProps) {
  const { importData, excludedRowIds, importNumber, existingContactMode } =
    state
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // ---------------------------------------------------------------------------
  // Derived state — all memoized so the per-row / per-column passes only re-run
  // when their inputs change, not on every render (tab switch, cell edit, mode
  // toggle). Computed BEFORE the early-return guard so hook order stays stable
  // (importData is null only before this step is reached).
  // ---------------------------------------------------------------------------
  const rows = useMemo(() => importData?.rows ?? [], [importData])
  const headers = useMemo(() => importData?.headers ?? [], [importData])
  const activeColumns = useMemo(() => getActiveColumns(headers), [headers])

  // Validation issues — the non-trivial pass over every row.
  const validationIssues: Record<string, RowIssue[]> = useMemo(
    () => validateContactRows(rows, headers),
    [rows, headers]
  )

  // In-file duplicates (same email on 2+ rows). Not errors, not excluded — the
  // backend merges them into a single contact.
  const inFileDuplicateRowIds = useMemo(
    () => findInFileDuplicateRowIds(rows, headers),
    [rows, headers]
  )

  // Errored rows + CRM-duplicate rows, collected in one pass.
  const { errorRowIds, crmDuplicateRowIds } = useMemo(() => {
    const errors = new Set<string>()
    const crm = new Set<string>()
    for (const r of rows) {
      const id = r[InternalColumns.ROW_ID]
      if (rowHasError(validationIssues[id] ?? [])) errors.add(id)
      if (r[InternalColumns.IS_EXISTING_CRM_CONTACT] === true) crm.add(id)
    }
    return { errorRowIds: errors, crmDuplicateRowIds: crm }
  }, [rows, validationIssues])

  // Duplicates tab = in-file ∪ CRM duplicates.
  const duplicateRowIds = useMemo(
    () => new Set<string>([...inFileDuplicateRowIds, ...crmDuplicateRowIds]),
    [inFileDuplicateRowIds, crmDuplicateRowIds]
  )

  // Effective exclusions = manual exclusions ∪ errored rows ∪ (in "keep" mode)
  // CRM duplicates. Errors are ALWAYS excluded (no user choice). In-file
  // duplicates are never excluded — the backend merges them into one contact.
  // CRM duplicates depend on the mode: "keep" skips them entirely (not imported,
  // not linked, not added to a campaign — existing contacts untouched), while
  // "update" includes them so the existing contacts are updated.
  const effectiveExcluded = useMemo(() => {
    const set = new Set<string>(excludedRowIds)
    errorRowIds.forEach((id) => set.add(id))
    if (existingContactMode === 'keep') {
      crmDuplicateRowIds.forEach((id) => set.add(id))
    }
    return set
  }, [excludedRowIds, errorRowIds, crmDuplicateRowIds, existingContactMode])

  // Per-tab row sets, computed in a single pass over the rows.
  const { includedRows, excludedRows, errorRows, warningRows, duplicateRows } =
    useMemo(() => {
      const included: typeof rows = []
      const excluded: typeof rows = []
      const errored: typeof rows = []
      const warned: typeof rows = []
      const duplicated: typeof rows = []
      for (const r of rows) {
        const id = r[InternalColumns.ROW_ID]
        if (effectiveExcluded.has(id)) excluded.push(r)
        else included.push(r)
        if (errorRowIds.has(id)) errored.push(r)
        if (rowHasWarning(validationIssues[id] ?? [])) warned.push(r)
        if (duplicateRowIds.has(id)) duplicated.push(r)
      }
      return {
        includedRows: included,
        excludedRows: excluded,
        errorRows: errored,
        warningRows: warned,
        duplicateRows: duplicated,
      }
    }, [
      rows,
      effectiveExcluded,
      errorRowIds,
      validationIssues,
      duplicateRowIds,
    ])

  const tabRows = useMemo(() => {
    const map: Record<FilterTab, typeof rows> = {
      all: rows,
      included: includedRows,
      excluded: excludedRows,
      errors: errorRows,
      warnings: warningRows,
      duplicates: duplicateRows,
    }
    return map[activeTab]
  }, [
    activeTab,
    rows,
    includedRows,
    excludedRows,
    errorRows,
    warningRows,
    duplicateRows,
  ])

  // Per-column error/warning markers for the active tab view — the most
  // expensive pass (columns × visible rows × issues), so memoize it.
  const colMarkers = useMemo(() => {
    const markers: Record<string, { errors: number; warnings: number }> = {}
    for (const col of activeColumns) {
      let errors = 0
      let warnings = 0
      for (const r of tabRows) {
        for (const issue of validationIssues[r[InternalColumns.ROW_ID]] ?? []) {
          if (issue.field !== col.mapped) continue
          if (issue.severity === 'error') errors++
          else warnings++
        }
      }
      markers[col.mapped] = { errors, warnings }
    }
    return markers
  }, [activeColumns, tabRows, validationIssues])

  // Guard: importData must be set before this step is reached.
  if (!importData) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <p className="text-sm text-fg-secondary">No import data available.</p>
      </div>
    )
  }

  // Non-null alias so handlers below capture the narrowed type.
  const data = importData

  const issuesFor = (rowId: string): RowIssue[] => validationIssues[rowId] ?? []
  /** Classify a duplicate row as in-file, CRM, or both. */
  const duplicateKindFor = (rowId: string): DuplicateKind => ({
    inFile: inFileDuplicateRowIds.has(rowId),
    crm: crmDuplicateRowIds.has(rowId),
  })
  const isEffectivelyExcluded = (rowId: string) => effectiveExcluded.has(rowId)

  const TAB_DEFS: TabDef[] = [
    { key: 'all', label: 'All rows', count: rows.length },
    { key: 'included', label: 'Included', count: includedRows.length },
    { key: 'excluded', label: 'Excluded', count: excludedRows.length },
    { key: 'errors', label: 'Errors', count: errorRows.length },
    { key: 'warnings', label: 'Warnings', count: warningRows.length },
    { key: 'duplicates', label: 'Duplicates', count: duplicateRows.length },
  ]

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  /** Updates a single cell value (by ORIGINAL CSV key) and propagates to state. */
  function handleCellChange(rowId: string, originalKey: string, value: string) {
    const updated = setCellByRowId(data, rowId, originalKey, value)
    dispatch({ type: 'SET_IMPORT_DATA', data: updated })
  }

  /** Toggles whether a row is manually included or excluded from the import. */
  function handleToggleExclude(rowId: string) {
    dispatch({ type: 'TOGGLE_ROW_EXCLUDED', rowId })
  }

  /** Downloads the errored rows as a CSV so the user can fix and re-upload. */
  function handleDownloadFailed() {
    const labels = activeColumns.map((c) => fieldLabel(c.mapped))
    const header = [...labels, 'Issues'].join(',')
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`
    const lines = errorRows.map((r) => {
      const id = r[InternalColumns.ROW_ID]
      const cells = activeColumns.map((c) =>
        escape(String(r[c.original] ?? ''))
      )
      const messages = issuesFor(id)
        .filter((i) => i.severity === 'error')
        .map((i) => i.message)
        .join('; ')
      return [...cells, escape(messages)].join(',')
    })
    const csv = [header, ...lines].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'failed_contact_rows.csv'
    a.click()
    window.URL.revokeObjectURL(url)
    a.remove()
  }

  /** Persists current edits + effective exclusions, then advances to import. */
  async function handleNext() {
    if (!importNumber) return
    setIsSaving(true)
    setSaveError(null)
    try {
      await updateImportPreview(importNumber, {
        import_data: { data: toRowsJSON(data) },
      })
      // Persist the effective excluded set so ReviewImportStep imports exactly the
      // rows shown as included here (manual exclusions + skipped error/dupe rows).
      dispatch({ type: 'SET_EXCLUDED_ROWS', rowIds: effectiveExcluded })
      dispatch({ type: 'SET_STEP', step: 'review' })
    } catch {
      setSaveError('Failed to save changes. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const showDecisionBar = errorRows.length > 0 || crmDuplicateRowIds.size > 0
  const willImport = includedRows.length
  const nextDisabled = isSaving || willImport === 0

  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-8 py-4">
        {/* Filter tabs + row count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 rounded-lg bg-surface-sunken p-1">
            {TAB_DEFS.map((tab) => {
              const isActive = activeTab === tab.key
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'flex h-[30px] items-center justify-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-surface text-fg shadow-sm'
                      : 'text-fg-secondary hover:text-fg'
                  )}
                >
                  {tab.label}
                  <span
                    className={cn(
                      'tabular-nums',
                      isActive ? 'text-fg-secondary' : 'text-fg-muted'
                    )}
                  >
                    {tab.count}
                  </span>
                </button>
              )
            })}
          </div>
          <span className="text-xs text-fg-muted">
            {tabRows.length} of {rows.length} rows shown
          </span>
        </div>

        {/* Table */}
        {tabRows.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-fg-muted">No rows in this view.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-line">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-surface-sunken">
                    <th
                      scope="col"
                      className="w-16 shrink-0 border-b border-line px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-fg-muted"
                    >
                      Include
                    </th>
                    <th
                      scope="col"
                      className="border-b border-line px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-fg-muted"
                    >
                      #
                    </th>
                    {activeColumns.map((col) => {
                      const markers = colMarkers[col.mapped] ?? {
                        errors: 0,
                        warnings: 0,
                      }
                      return (
                        <th
                          key={col.mapped}
                          scope="col"
                          className="border-b border-line px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-fg-muted"
                        >
                          <span className="flex items-center gap-1.5 whitespace-nowrap">
                            {fieldLabel(col.mapped)}
                            {markers.errors > 0 && (
                              <span className="size-1.5 shrink-0 rounded-full bg-error" />
                            )}
                            {markers.warnings > 0 && (
                              <span className="size-1.5 shrink-0 rounded-full bg-warning" />
                            )}
                          </span>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tabRows.map((row, idx) => {
                    const rowId = row[InternalColumns.ROW_ID]
                    const issues = issuesFor(rowId)
                    const isExcluded = isEffectivelyExcluded(rowId)
                    const hasError = errorRowIds.has(rowId)
                    const hasWarning = rowHasWarning(issues)
                    const isDuplicate = duplicateRowIds.has(rowId)
                    const duplicateKind = isDuplicate
                      ? duplicateKindFor(rowId)
                      : null

                    // For duplicate rows, synthesize a display-only 'duplicate'
                    // issue on each populated email cell so the email itself reads
                    // red and surfaces the in-file/CRM explanation on hover.
                    const renderIssues: RowIssue[] = duplicateKind
                      ? [
                          ...issues,
                          ...activeColumns
                            .filter(
                              (c) =>
                                EMAIL_LOGICAL_FIELDS.includes(c.mapped) &&
                                String(row[c.original] ?? '').trim() !== ''
                            )
                            .map((c) => ({
                              field: c.mapped,
                              message: duplicateTooltip(
                                duplicateKind,
                                existingContactMode
                              ),
                              severity: 'duplicate' as const,
                            })),
                        ]
                      : issues

                    return (
                      <tr
                        key={rowId}
                        className={cn(
                          'border-b border-line transition-colors last:border-0',
                          isExcluded
                            ? 'bg-surface-sunken opacity-60'
                            : hasError
                              ? 'bg-error-bg/40'
                              : hasWarning
                                ? 'bg-warning-bg/30'
                                : isDuplicate
                                  ? 'bg-surface-hover'
                                  : 'bg-surface hover:bg-surface-hover'
                        )}
                      >
                        {/* Include / exclude toggle */}
                        <td className="w-16 px-3 py-1.5">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={() => handleToggleExclude(rowId)}
                            className="size-4 cursor-pointer accent-brand"
                            aria-label={
                              isExcluded
                                ? 'Include this row'
                                : 'Exclude this row'
                            }
                          />
                        </td>
                        {/* 1-based row index */}
                        <td className="px-3 py-1.5 text-xs text-fg-muted">
                          {idx + 1}
                        </td>
                        {activeColumns.map((col) => (
                          <td key={col.mapped} className="px-1.5 py-1">
                            {/* Read value by ORIGINAL CSV key; issues by mapped name */}
                            <EditableCell
                              value={String(row[col.original] ?? '')}
                              issues={renderIssues}
                              field={col.mapped}
                              onChange={(v) =>
                                handleCellChange(rowId, col.original, v)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legend */}
        <ValidationLegend />
      </div>

      {/* Decision bar: errors (always excluded) + CRM-duplicate keep/update mode */}
      {showDecisionBar && (
        <DecisionBar
          errorCount={errorRows.length}
          crmDuplicateCount={crmDuplicateRowIds.size}
          existingContactMode={existingContactMode}
          onExistingContactModeChange={(mode) =>
            dispatch({ type: 'SET_EXISTING_CONTACT_MODE', mode })
          }
          willImport={willImport}
          total={rows.length}
          onDownloadFailed={handleDownloadFailed}
        />
      )}

      {/* Footer: navigation */}
      <div className="flex shrink-0 items-center justify-between border-t border-line bg-surface px-8 py-4">
        <button
          type="button"
          className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-fg transition-colors hover:bg-surface-hover"
          onClick={() => dispatch({ type: 'SET_STEP', step: 'map' })}
          disabled={isSaving}
        >
          Back
        </button>

        <div className="flex items-center gap-3">
          {saveError !== null && (
            <p role="alert" className="text-sm text-error">
              {saveError}
            </p>
          )}
          {willImport === 0 && !isSaving && (
            <p className="flex items-center gap-1.5 text-sm text-fg-secondary">
              <PiWarning className="size-4 shrink-0 text-warning" />
              No rows will be imported — include at least one row to continue.
            </p>
          )}
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand/90 disabled:opacity-40"
            disabled={nextDisabled}
            onClick={handleNext}
          >
            {isSaving && <PiCircleNotch className="size-4 animate-spin" />}
            {isSaving ? 'Saving…' : 'Next: Import'}
          </button>
        </div>
      </div>
    </div>
  )
}
